import axios, { type AxiosInstance, type AxiosResponse, type AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '../types';
import { getDeactivatedAccountMessage, redirectToLoginAfterAuthFailure } from '@/utils/authSession';

const transformUser = (user: any) => ({
  id: user.id,
  email: user.email,
  role: user.role,
  first_name: user.first_name,
  last_name: user.last_name,
  company_name: user.company_name,
  phone_country_code: user.phone_country_code,
  phone_number: user.phone_number,
  is_active: user.is_active,
  is_deleted: user.is_deleted ?? false,
  deleted_at: user.deleted_at,
  must_change_password: user.must_change_password ?? false,
  email_verified_at: user.email_verified_at,
  created_at: user.created_at,
  updated_at: user.updated_at,
  last_login_at: user.last_login_at,
  totp_enabled: user.totp_enabled ?? false,
});

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    const configuredTimeout = Number(import.meta.env.VITE_API_TIMEOUT);

    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000',
      // Axios uses 0 to disable request timeouts.
      timeout: Number.isFinite(configuredTimeout) && configuredTimeout >= 0
        ? configuredTimeout
        : 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token_localstorage = localStorage.getItem('auth_token');
        const token = sessionStorage.getItem('auth_token') || token_localstorage;
        if (token === token_localstorage) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle common errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        // Redirect to login on 401, but NOT for auth endpoints themselves
        // (login, verify-2fa, 2fa management) — those return 401 as meaningful
        // error responses that the UI needs to handle.
        const url = error.config?.url ?? '';
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/verify-2fa') ||
          url.includes('/auth/2fa/');

        const responseStatus = error.response?.status;
        const deactivatedMessage = getDeactivatedAccountMessage(error.response?.data);
        const shouldRedirectToLogin =
          responseStatus === 401 || (responseStatus === 403 && Boolean(deactivatedMessage));

        if (shouldRedirectToLogin && !isAuthEndpoint) {
          redirectToLoginAfterAuthFailure(error.response?.data);
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(url, { params });
      
      // Handle different backend response formats
      if (response.data?.success !== undefined) {
        // Backend returns success field directly (like users, domains, etc.)
        
        // Special handling for users endpoint
        if (url.includes('/admin/users') && response.data.users) {
          const transformedUsers = response.data.users.map(transformUser);
          
          return {
            success: response.data.success,
            data: {
              users: transformedUsers,
              total: response.data.total
            } as T
          };
        }
        
        return {
          success: response.data.success,
          data: response.data.success ? response.data as T : undefined,
          error: response.data.success ? undefined : {
            code: 'API_ERROR',
            message: response.data.error || 'Request failed',
            timestamp: new Date().toISOString()
          }
        };
      }
      
      // Handle analytics endpoints that return data directly without success wrapper
      if (url.includes('/analytics/summary')) {
        // Transform snake_case to camelCase for analytics summary
        const transformedData = {
          total_requests: response.data.total_requests,
          successful_requests: response.data.successful_requests,
          success_rate: response.data.success_rate,
          average_response_time: response.data.average_response_time,
          cache_hit_rate: response.data.cache_hit_rate,
          top_domains: response.data.top_domains?.map((domain: any) => ({
            domain: domain.domain,
            request_count: domain.request_count
          })) || [],
          errors_by_type: response.data.errors_by_type?.map((error: any) => ({
            error_type: error.error_type,
            count: error.count
          })) || [],
          period_start: response.data.period_start,
          period_end: response.data.period_end
        };
        
        return {
          success: true,
          data: transformedData as T
        };
      }
      
      // Handle other analytics endpoints that return data directly
      if (url.includes('/analytics/')) {
        return {
          success: true,
          data: response.data as T
        };
      }
      
      // Handle reels endpoints that return data directly as an array
      if (url.includes('/admin/reels/')) {
        return {
          success: true,
          data: response.data as T
        };
      }

      // Handle 2FA status endpoint that returns data directly
      if (url.includes('/auth/2fa/')) {
        return {
          success: true,
          data: response.data as T
        };
      }

      // Handle auth/profile which returns a plain UserProfile object (no success wrapper)
      if (url.includes('/auth/profile')) {
        return {
          success: true,
          data: response.data as T
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error, url);
    }
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, data, config);
      
      // Handle reels endpoints that return data directly
      if (url.includes('/admin/reels/')) {
        return {
          success: true,
          data: response.data as T
        };
      }

      // Handle 2FA endpoints that return data directly (no success wrapper)
      if (url.includes('/auth/2fa/') || url.includes('/auth/verify-2fa')) {
        return {
          success: true,
          data: response.data as T
        };
      }

      // Handle login endpoint — two possible shapes:
      //   a) { requires_2fa: true, temp_token } — 2FA challenge, no success field
      //   b) { success: true, token, user }      — full login
      if (url.includes('/auth/login')) {
        // 2FA challenge path
        if (response.data?.requires_2fa) {
          return {
            success: true,
            data: response.data as T,
          };
        }
        // Full login path
        if (response.data?.success !== undefined) {
          return {
            success: response.data.success,
            data: response.data.success ? {
              token: response.data.token,
              user: response.data.user,
            } as T : undefined,
            error: response.data.success ? undefined : {
              code: 'LOGIN_FAILED',
              message: response.data.error || response.data.detail || 'Login failed',
              timestamp: new Date().toISOString(),
            },
          };
        }
        // Fallback — return raw (covers unexpected shapes)
        return { success: true, data: response.data as T };
      }
      
      // Handle other endpoints that return success field directly
      if (response.data?.success !== undefined) {
        // Transform user data if present
        let transformedData = response.data;
        if (response.data.user) {
          transformedData = {
            ...response.data,
            user: transformUser(response.data.user)
          };
        }
        
        return {
          success: response.data.success,
          data: response.data.success ? transformedData as T : undefined,
          error: response.data.success ? undefined : {
            code: 'API_ERROR',
            message: response.data.error || 'Request failed',
            timestamp: new Date().toISOString()
          }
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error, url);
    }
  }

  async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put(url, data);
      
      // Handle reels endpoints that return data directly
      if (url.includes('/admin/reels/')) {
        return {
          success: true,
          data: response.data as T
        };
      }
      
      // Handle different backend response formats
      if (response.data?.success !== undefined) {
        // Transform user data if present
        let transformedData = response.data;
        if (response.data.user) {
          transformedData = {
            ...response.data,
            user: transformUser(response.data.user)
          };
        }
        
        return {
          success: response.data.success,
          data: response.data.success ? transformedData as T : undefined,
          error: response.data.success ? undefined : {
            code: 'API_ERROR',
            message: response.data.error || 'Request failed',
            timestamp: new Date().toISOString()
          }
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error, url);
    }
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete(url);
      
      // Handle reels endpoints - DELETE returns 204 No Content
      if (url.includes('/admin/reels/')) {
        return {
          success: true,
          data: { success: true } as T
        };
      }
      
      // Handle different backend response formats
      if (response.data?.success !== undefined) {
        // Backend returns success field directly
        return {
          success: response.data.success,
          data: response.data.success ? response.data as T : undefined,
          error: response.data.success ? undefined : {
            code: 'API_ERROR',
            message: response.data.error || 'Request failed',
            timestamp: new Date().toISOString()
          }
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error, url);
    }
  }

  private handleError(error: unknown, requestUrl = ''): ApiResponse<never> {
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/verify-2fa') ||
      requestUrl.includes('/auth/2fa/') ||
      requestUrl.includes('/auth/request-password-reset') ||
      requestUrl.includes('/auth/reset-password');

    // Handle network errors
    if (error && typeof error === 'object' && 'code' in error) {
      const networkError = error as { code?: string };
      if (networkError.code === 'NETWORK_ERROR' || networkError.code === 'ERR_NETWORK') {
        return {
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server. Please check your internet connection.',
            timestamp: new Date().toISOString(),
          },
        };
      }
    }

    // Handle timeout errors
    if (error && typeof error === 'object' && 'code' in error) {
      const timeoutError = error as { code?: string };
      if (timeoutError.code === 'ECONNABORTED') {
        return {
          success: false,
          error: {
            code: 'TIMEOUT_ERROR',
            message: 'Request timed out. Please try again.',
            timestamp: new Date().toISOString(),
          },
        };
      }
    }

    // Handle axios errors with response
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError<any>;
      const status = axiosError.response?.status;
      const responseData = axiosError.response?.data;

      if (status === 401 && !isAuthEndpoint) {
        return {
          success: false,
          error: {
            code: 'UNAUTHORIZED',
            message: 'Your session has expired. Please sign in again.',
            timestamp: new Date().toISOString(),
          },
        };
      }

      if (responseData) {
        const data = responseData as Record<string, any>;
        const responseMessage = data.detail ?? data.message ?? data.error;

        if (data.success === true) {
          return data as ApiResponse<never>;
        }

        const nestedMessage =
          typeof data.error === 'object' && data.error
            ? data.error.message ?? data.error.detail ?? data.error.error
            : undefined;

        let code = `HTTP_${status ?? 'UNKNOWN'}`;
        let message =
          responseMessage ??
          nestedMessage ??
          'An unexpected error occurred';

        if (status === 401) {
          code = 'UNAUTHORIZED';
          if (requestUrl.includes('/auth/login')) {
            message = 'Invalid email or password. Please try again.';
          } else if (requestUrl.includes('/auth/verify-2fa')) {
            message = 'Invalid authentication code. Please try again.';
          } else if (requestUrl.includes('/auth/2fa/disable')) {
            message = 'Incorrect password. Please try again.';
          } else if (!message || message === 'An unexpected error occurred') {
            message = 'You are not authorized to perform this action.';
          }
        } else if (status === 409) {
          code = 'CONFLICT';
          if (requestUrl.includes('/api/admin/users') || requestUrl.includes('/auth/register')) {
            message = message || 'A user with this email already exists.';
          }
        } else if (status === 404) {
          code = 'NOT_FOUND';
          message = message || 'The requested resource was not found.';
        } else if (status === 400) {
          code = 'BAD_REQUEST';
          message = message || 'Invalid request. Please check your input.';
        } else if (status === 422) {
          code = 'VALIDATION_ERROR';
          message = message || 'Validation failed. Please check your input.';
        } else if (status === 429) {
          code = 'RATE_LIMIT';
          message = message || 'Too many requests. Please try again later.';
        } else if (status === 500) {
          code = 'SERVER_ERROR';
          message = message || 'Internal server error. Please try again later.';
        } else if (status === 502 || status === 503 || status === 504) {
          code = 'SERVICE_UNAVAILABLE';
          message = message || 'Service temporarily unavailable. Please try again later.';
        }

        return {
          success: false,
          error: {
            code,
            message,
            timestamp: new Date().toISOString(),
          },
        };
      }

      // Handle HTTP status codes without proper error response
      let message = 'An unexpected error occurred';
      let code = 'UNKNOWN_ERROR';

      switch (status) {
        case 400:
          code = 'BAD_REQUEST';
          message = 'Invalid request. Please check your input.';
          break;
        case 401:
          code = 'UNAUTHORIZED';
          message = requestUrl.includes('/auth/login')
            ? 'Invalid email or password. Please try again.'
            : 'You are not authorized to perform this action.';
          break;
        case 403:
          code = 'FORBIDDEN';
          message = 'You do not have permission to access this resource.';
          break;
        case 404:
          code = 'NOT_FOUND';
          message = 'The requested resource was not found.';
          break;
        case 409:
          code = 'CONFLICT';
          message = requestUrl.includes('/api/admin/users') || requestUrl.includes('/auth/register')
            ? 'A user with this email already exists.'
            : 'A conflict occurred. The requested resource may already exist.';
          break;
        case 422:
          code = 'VALIDATION_ERROR';
          message = 'Validation failed. Please check your input.';
          break;
        case 429:
          code = 'RATE_LIMIT';
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          code = 'SERVER_ERROR';
          message = 'Internal server error. Please try again later.';
          break;
        case 502:
        case 503:
        case 504:
          code = 'SERVICE_UNAVAILABLE';
          message = 'Service temporarily unavailable. Please try again later.';
          break;
      }

      return {
        success: false,
        error: {
          code,
          message,
          timestamp: new Date().toISOString(),
        },
      };
    }
    
    // Handle generic errors
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: errorMessage,
        timestamp: new Date().toISOString(),
      },
    };
  }
}

export const apiClient = new ApiClient();
